import type { TFunction } from 'i18next';
import { useTranslation } from 'react-i18next';

import type { WorkloadRow } from '../../types';
import { Avatar } from '@/shared/ui/atoms/Avatar';
import { Mono } from '@/shared/ui/atoms/Mono';
import { ProgressBar } from '@/shared/ui/atoms/ProgressBar';
import { capacityTone } from '@/shared/ui/atoms/ProgressBar/capacityTone';
import { StatusPill } from '@/shared/ui/molecules/StatusPill';
import { DataTable, type Column } from '@/shared/ui/organisms/DataTable';
import './WorkloadTable.styl';

type Props = { rows: WorkloadRow[] };

const buildColumns = (t: TFunction): Column<WorkloadRow>[] => [
  {
    key: 'name',
    header: t('insights.workload.name'),
    width: '32%',
    render: (m) => (
      <div className="f-workload__person">
        <Avatar initials={m.initials} color={m.avatarColor} size={30} />
        <div>
          <div className="f-workload__name">{m.name}</div>
          <div className="f-workload__role">{m.role}</div>
        </div>
      </div>
    )
  },
  { key: 'activeTasks', header: t('insights.workload.active'), mono: true, render: (m) => m.activeTasks },
  {
    key: 'overdueTasks',
    header: t('insights.workload.overdue'),
    render: (m) => <Mono className={m.overdueTasks >= 4 ? 'f-workload__overdue' : ''}>{m.overdueTasks}</Mono>
  },
  {
    key: 'capacityPct',
    header: t('insights.workload.capacity'),
    width: '24%',
    render: (m) => (
      <div className="f-workload__cap">
        <ProgressBar value={m.capacityPct} tone={capacityTone(m.capacityPct)} label={t('insights.workload.capacityAria', { name: m.name, pct: m.capacityPct })} />
        <Mono className="f-workload__cap-pct">{m.capacityPct}%</Mono>
      </div>
    )
  },
  { key: 'workloadStatus', header: t('insights.workload.status'), render: (m) => <StatusPill status={m.workloadStatus} /> }
];

export function WorkloadTable({ rows }: Props) {
  const { t } = useTranslation();
  return <DataTable columns={buildColumns(t)} rows={rows} rowKey={(m) => m.userId} empty={t('insights.workload.empty')} />;
}
